'use client';

import { useCallback, useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { FormRenderer } from '@jlg-website/form/renderer/client';
import type {
  Answers,
  CallbackSettingsInput,
  FormConfig,
  FormInput,
  FormLocale,
  FormSubmitPayload,
  Plugins,
} from '@jlg-website/shared';
import type { FormProps, FormVariant } from './type';
import { useLeadForm } from '@/hooks/use-lead-form';
import { buildRenderCompletion } from './completion';
import { showsCallbackScheduler } from './completion/shows-callback-scheduler';
import { submitCallback } from './callback/submit-callback';
import type { SubmitResult } from './lead/submit-lead';

/** What the completion screen needs from the lead that was just created. */
type SettledLead = Pick<SubmitResult, 'lead_id' | 'contact' | 'is_qualified' | 'is_duplicate'>;

type FormClientProps = {
  schema: FormInput;
  variant?: FormVariant;
  config?: FormConfig;
  locale?: FormLocale;
  /** Answers to pre-select, decoded from the URL server-side (see `answer-query`). */
  initialAnswers?: Answers;
  completionAction?: FormProps['completionAction'];
  /** Page the visitor came from; when set, the first step's Prev navigates back here. */
  backRoute?: string;
  /** Present only when the form schedules callbacks; drives the scheduler on the last step. */
  callbackSettings?: CallbackSettingsInput | null;
};

export default function FormClient({
  schema,
  variant,
  config,
  locale,
  initialAnswers,
  completionAction,
  backRoute,
  callbackSettings,
}: FormClientProps) {
  const router = useRouter();
  // Validators, marketing events and the submit path are shared with the mass-tort hero client.
  const {
    config: configWithValidators,
    handleSubmit,
    handleStepNext,
    handleReachLastStep,
  } = useLeadForm(schema, config);

  // The scheduler greets the visitor by the number they just gave, reads their time zone from
  // the ZIP, and posts against the lead that was just created — all of it lives in the submit
  // result, so it is kept once the lead settles. Its outcome comes along: it is what decides
  // whether the scheduler replaces the completion message at all.
  const [lead, setLead] = useState<SettledLead | null>(null);

  const handleSubmitAndKeepLead = useCallback(
    async (payload: FormSubmitPayload, plugins: Plugins) => {
      const result = await handleSubmit(payload, plugins);
      setLead({
        lead_id: result.lead_id,
        contact: result.contact,
        is_qualified: result.is_qualified,
        is_duplicate: result.is_duplicate,
      });

      return result;
    },
    [handleSubmit]
  );

  /**
   * The scheduler carries the closing button itself, on the panel that ends its flow, so the form's
   * chrome must not draw a second one over the steps that lead there.
   *
   * Decided from the settled lead rather than toggled as the visitor schedules: the label also
   * picks which wrapper the result step renders in, and swapping wrappers mid-flow would remount
   * the scheduler and throw away the booking it is showing. `lead` is set inside the awaited
   * `onSubmit`, before the renderer has a completion to draw, so this never flips after the fact.
   */
  const schedulerTakesOver = showsCallbackScheduler(callbackSettings, lead);
  const shellCompletionAction = schedulerTakesOver ? undefined : completionAction;

  const closeForm = useCallback(() => {
    if (completionAction) router.push(completionAction.href);
  }, [router, completionAction]);

  // Only when the visitor arrived from the same-slug page (its answers are in the URL) does the
  // first step get a Prev that takes them back there; a direct visit leaves it disabled.
  const handleFirstStepPrev = useCallback(() => {
    if (backRoute) router.push(backRoute);
  }, [router, backRoute]);

  // Triggers the event for the first step on Form load
  useEffect(() => {
    handleStepNext({
      answers: {},
      is_qualified: false,
      current_step: 1,
      is_finished: false,
    } as FormSubmitPayload);
  }, [handleStepNext]);

  return (
    <FormRenderer
      schema={schema}
      config={configWithValidators}
      variant={variant}
      locale={locale}
      autoNext
      // Pre-selected from the URL token (server-decoded); with `autoNext`, a fully answered leading
      // step advances on its own.
      initialAnswers={initialAnswers}
      onSubmit={handleSubmitAndKeepLead}
      onStepNext={handleStepNext}
      // A `redirect` fires `onRedirect` (not `onStepNext`), so wire the same progress event here
      // to keep emitting `emitStepProgress` when the visitor is redirected.
      onRedirect={handleStepNext}
      onReachLastStep={handleReachLastStep}
      enableNextOnValidate={false}
      renderCompletion={buildRenderCompletion(
        configWithValidators.completionMessage,
        callbackSettings && lead
          ? {
              settings: callbackSettings,
              contact: lead.contact,
              locale,
              onConfirm: (schedule) => submitCallback(lead.lead_id, schedule),
              onCallNow: (schedule) => submitCallback(lead.lead_id, schedule),
              completionAction: completionAction
                ? { label: completionAction.label, onClick: closeForm }
                : undefined,
            }
          : null
      )}
      completionActionLabel={shellCompletionAction?.label}
      onCompletionAction={shellCompletionAction ? closeForm : undefined}
      enablePrevOnFirstStep={Boolean(backRoute)}
      onFirstStepPrev={handleFirstStepPrev}
    />
  );
}
